import dayjs from "dayjs"
import { merge } from "lodash"
import get from "lodash/get"
import { type ReactNode } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  COMPARATORS,
  type FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  StudentConstructor,
  StudentSkillType,
} from "#customTypes/composedTypes/studentTypes"
import {
  TryoutConstructor,
  TryoutProgressFormData,
  TryoutStatus,
} from "#customTypes/composedTypes/tryoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import { COLORS } from "#styles/colors"

import { getNow } from "./dateHelpers"
import { getMissingSkillLevelForEnrollment } from "./enrollmentSkillLevelHelpers"
import { SEMESTER_DATES } from "./hardcodedSettings"
import { addOneSemester, getCurrentSemester } from "./semesterHelpers"
import {
  getAllSkillLevelsAsOf,
  type StudentSkillSource,
} from "./studentSkillHelpers"

export const DEFAULT_TRYOUT_PROGRESS_FORM_DATA = {
  // These should already be filled out from the initial AddTryoutForm
  id: "",
  studentId: "",
  classId: "",
  sessionId: "",
  semester: getCurrentSemester(),

  tryoutFilterDate: getNow(),
  tryoutStatus: TryoutStatus.PENDING,

  // Step 0: Scheduled (handled by AddTryoutForm, editable in TryoutProgressForm)
  step0: {
    isTryoutFeePaid: false,
    hasSelectedSession: true, // Always true if we have a sessionId
  },
  // Step 1: Arrived
  step1: {
    isTourCompleted: false,
    teacherHasEvalForm: false,
    hasEmergencyContact: false,
    hasSignedReleaseForm: false,
  },
  // Step 2: In Class
  step2: {
    isEvalScanned: false,
    isLevelUpdated: false,
    didPassTryout: undefined, // Neutral - user must explicitly select pass or fail
  },
  // Step 3: Pickup
  step3: {
    hasEvalFeedback: false,
    hasGivenSwag: false,
    enrollmentReceiptSent: false,
    hasSelectedSessions: false,
  },
} as TryoutProgressFormData

export type TryoutStepProgress = {
  step0: {
    isTryoutFeePaid: boolean
    hasSelectedSession: boolean
  }
  step1: {
    isTourCompleted: boolean
    teacherHasEvalForm: boolean
    hasEmergencyContact: boolean
    hasSignedReleaseForm: boolean
  }
  step2: {
    isEvalScanned: boolean
    isResultDecided: boolean // true if pass or fail is explicitly selected
    isResultDocumented: boolean // true if (passed AND level set) OR (failed AND reason provided)
  }
  step3: {
    hasEvalFeedback: boolean
    hasGivenSwag: boolean
    enrollmentReceiptSent: boolean
    hasSelectedSessions: boolean
  }
}

export const getStepProgress = (
  data: TryoutProgressFormData | TryoutConstructor
): TryoutStepProgress => {
  // Step 0: Scheduled
  const step0 = {
    isTryoutFeePaid: !!get(data, "step0.isTryoutFeePaid", false),
    hasSelectedSession: !!get(data, "sessionId", false), // Has session = step0 complete
  }

  // Step 1: Arrived
  const step1 = {
    isTourCompleted: !!get(data, "step1.isTourCompleted", false),
    teacherHasEvalForm: !!get(data, "step1.teacherHasEvalForm", false),
    hasEmergencyContact: !!get(data, "step1.hasEmergencyContact", false),
    hasSignedReleaseForm: !!get(data, "step1.hasSignedReleaseForm", false),
  }

  // Step 2: In Class
  // didPassTryout: undefined = not decided, true = passed, false = failed
  const didPassTryout = get(data, "step2.didPassTryout", undefined) as
    | boolean
    | undefined
  const isLevelUpdated = !!get(data, "step2.isLevelUpdated", false)
  const failureReason = get(data, "step2.failureReason", "") as string

  // Result is documented when:
  // - Passed AND level is updated, OR
  // - Failed AND reason is provided
  const isResultDocumented =
    didPassTryout === true
      ? isLevelUpdated
      : didPassTryout === false
        ? !!failureReason?.trim()
        : false

  const step2 = {
    isEvalScanned: !!get(data, "step2.isEvalScanned", false),
    isResultDecided: didPassTryout === true || didPassTryout === false,
    isResultDocumented,
  }

  // Step 3: Pickup
  const step3 = {
    hasEvalFeedback: !!get(data, "step3.hasEvalFeedback", false),
    hasGivenSwag: !!get(data, "step3.hasGivenSwag", false),
    enrollmentReceiptSent: !!get(data, "step3.enrollmentReceiptSent", false),
    hasSelectedSessions: !!get(data, "step3.hasSelectedSessions", false),
  }

  return {
    step0,
    step1,
    step2,
    step3,
  }
}

export const getCurrentStep = (
  data: TryoutProgressFormData | TryoutConstructor
): 0 | 1 | 2 | 3 => {
  const { step0, step1, step2 } = getStepProgress(data)
  if (!getIsStepCompleted(step0)) return 0
  if (!getIsStepCompleted(step1)) return 1
  if (!getIsStepCompleted(step2)) return 2
  return 3
}

export const getIsStepCompleted = (
  stepData: Record<string, unknown>
): boolean => {
  const booleanValues = Object.values(stepData).filter(
    (v): v is boolean => typeof v === "boolean"
  )
  return booleanValues.length > 0 && booleanValues.every(Boolean)
}

/**
 * Three-state tryout outcome derived from `step2.didPassTryout`, where
 * `undefined` (not yet decided) is meaningful and must not collapse into
 * "failed". Shared by the Tryouts table Result column and the progress-form
 * header so both surfaces show identical copy.
 */
export const TRYOUT_RESULTS = {
  PENDING: "PENDING",
  PASSED: "PASSED",
  FAILED: "FAILED",
} as const
export type TryoutResult = (typeof TRYOUT_RESULTS)[keyof typeof TRYOUT_RESULTS]

export const TRYOUT_RESULT_LABELS: Record<TryoutResult, string> = {
  [TRYOUT_RESULTS.PENDING]: "Pending",
  [TRYOUT_RESULTS.PASSED]: "Passed",
  [TRYOUT_RESULTS.FAILED]: "Failed",
}

/** Sort rank so a Result column orders Pending → Passed → Failed. */
export const TRYOUT_RESULT_SORT_RANK: Record<TryoutResult, number> = {
  [TRYOUT_RESULTS.PENDING]: 0,
  [TRYOUT_RESULTS.PASSED]: 1,
  [TRYOUT_RESULTS.FAILED]: 2,
}

export const getTryoutResult = (
  didPassTryout: boolean | null | undefined
): TryoutResult => {
  if (didPassTryout === true) return TRYOUT_RESULTS.PASSED
  if (didPassTryout === false) return TRYOUT_RESULTS.FAILED
  return TRYOUT_RESULTS.PENDING
}

export const TRYOUT_ENROLLMENT_BLOCK_REASONS = {
  FAILED_TRYOUT: "FAILED_TRYOUT",
  MISSING_SKILL_LEVEL: "MISSING_SKILL_LEVEL",
} as const
export type TryoutEnrollmentBlockReason =
  (typeof TRYOUT_ENROLLMENT_BLOCK_REASONS)[keyof typeof TRYOUT_ENROLLMENT_BLOCK_REASONS]

export interface TryoutEnrollmentBlock {
  reason: TryoutEnrollmentBlockReason
  /** Step the user is sent to; everything blocking enrollment is fixed there. */
  step: 2
  /** One-line headline, e.g. the Alert `message`. */
  message: string
  /** How to get unstuck, e.g. the Alert `description`. Always names a path. */
  description: string
}

/** `message` + `description` as a single sentence pair for error toasts. */
export const formatTryoutEnrollmentBlock = (
  block: TryoutEnrollmentBlock
): string => `${block.message} ${block.description}`

/**
 * Why Step 3 may NOT enroll the student from this tryout right now, or `null`
 * when enrollment may proceed. Single source of truth for the Step 3 warning
 * Alert and the `handleFinish` submit gate so the two can't disagree.
 *
 * Checked in order:
 *
 * 1. FAILED_TRYOUT — `didPassTryout === false`. A failed tryout is the
 *    teacher saying the student is not ready for this class, so it can't be
 *    the basis for an enrollment. Not a dead end: the user can mark it Passed
 *    in Step 2 (and set the level there), or enroll from the class roster.
 *    Skipped when the student already has a real enrollment in the class —
 *    then the save is editing an existing enrollment, not creating one.
 * 2. MISSING_SKILL_LEVEL — JARVIS-43, via `getMissingSkillLevelForEnrollment`.
 *    Step 2's dropdown only renders for a PASSED tryout, so the description
 *    is tailored to the result: passed → set it in Step 2; pending → mark
 *    Passed in Step 2 first; failed (reachable only with an existing
 *    enrollment) → set it on the student's profile or mark Passed.
 */
export const getTryoutEnrollmentBlock = ({
  didPassTryout,
  student,
  enrollmentClass,
  hasExistingEnrollment = false,
}: {
  didPassTryout: boolean | null | undefined
  student: (StudentSkillSource & { name?: string }) | null | undefined
  enrollmentClass:
    | (Pick<ClassConstructor, "group" | "isSummerCamp" | "semester"> & {
        name?: string
      })
    | null
    | undefined
  /** Student already has non-tryout/makeup attendance in `enrollmentClass`. */
  hasExistingEnrollment?: boolean
}): TryoutEnrollmentBlock | null => {
  const result = getTryoutResult(didPassTryout)
  const studentName = student?.name ?? "This student"
  const className = enrollmentClass?.name ?? "this class"

  if (result === TRYOUT_RESULTS.FAILED && !hasExistingEnrollment) {
    return {
      reason: TRYOUT_ENROLLMENT_BLOCK_REASONS.FAILED_TRYOUT,
      step: 2,
      message: `${studentName} did not pass this tryout, so they can't be enrolled from it.`,
      description:
        "If they should be enrolled anyway, mark the tryout as Passed in Step 2 (In Class) and set their skill level, or add them from the class roster instead.",
    }
  }

  const missingLevel = getMissingSkillLevelForEnrollment(
    student,
    enrollmentClass
  )
  if (!missingLevel) return null

  const description =
    result === TRYOUT_RESULTS.PASSED
      ? "Set it in Step 2 (In Class), then save again."
      : result === TRYOUT_RESULTS.PENDING
        ? "Mark the tryout as Passed in Step 2 (In Class) and set the level there, then save again."
        : "The tryout is marked Failed, so set the level on the student's profile (click their name above), or mark the tryout as Passed in Step 2 (In Class) and set it there."

  return {
    reason: TRYOUT_ENROLLMENT_BLOCK_REASONS.MISSING_SKILL_LEVEL,
    step: 2,
    message: `${studentName} needs a ${missingLevel.groupLabel} skill level before enrolling in ${className}.`,
    description,
  }
}

/**
 * Semester the Step 3 enrollment class picker should list classes for.
 * `isEnrollingNextSemester` (step3) mirrors makeup.isScheduledForNextSemester:
 * when set, families enroll into the semester after the tryout's instead of
 * joining the tail of the current one. Returns `undefined` when the tryout has
 * no semester, or when there is no configured semester after it.
 */
export const getEnrollmentSemester = (
  tryoutSemester: string | null | undefined,
  isEnrollingNextSemester: boolean | null | undefined
): string | undefined => {
  if (!tryoutSemester) return undefined
  if (!isEnrollingNextSemester) return tryoutSemester
  // addOneSemester maps an unknown semester to index 0 ("2016 A"); guard so a
  // malformed value can't silently list decade-old classes.
  const isKnownSemester = SEMESTER_DATES.some(([sem]) => sem === tryoutSemester)
  if (!isKnownSemester) return undefined
  return addOneSemester(tryoutSemester) ?? undefined
}

/**
 * Firestore query for the Step 3 enrollment class picker, or `null` when the
 * query must not run yet (the caller maps `null` to the `NULL_ID` guard).
 *
 * Both halves are mandatory: the resolved enrollment semester AND the tryout
 * class attribute that scopes it — `curriculumId` for the current semester,
 * `group` for the next one (curricula are per-semester, so the id can't carry
 * over). Refusing to build a partial query is what stops a missing semester
 * from degrading into a group-only query that would list that group's classes
 * from every term. Mirrors `useMakeupFormData`'s `NULL_QUERY_CONFIG` guard.
 */
export const buildEnrollmentClassQueryConfig = ({
  enrollmentSemester,
  isEnrollingNextSemester,
  tryoutClass,
}: {
  enrollmentSemester: string | null | undefined
  isEnrollingNextSemester: boolean | null | undefined
  tryoutClass:
    | Partial<Pick<ClassConstructor, "group" | "curriculumId">>
    | null
    | undefined
}): FirebaseQueryConfig | null => {
  if (!enrollmentSemester) return null

  const semester = { value: enrollmentSemester, comparator: COMPARATORS.EQUALS }

  if (isEnrollingNextSemester) {
    if (!tryoutClass?.group) return null
    return {
      queries: {
        semester,
        group: { value: tryoutClass.group, comparator: COMPARATORS.EQUALS },
      },
    }
  }

  if (!tryoutClass?.curriculumId) return null
  return {
    queries: {
      semester,
      curriculumId: {
        value: tryoutClass.curriculumId,
        comparator: COMPARATORS.EQUALS,
      },
    },
  }
}

export const getProgressIndicator = (tryout: TryoutConstructor): ReactNode => {
  const { step0, step1, step2, step3 } = getStepProgress(tryout)
  const steps = [step0, step1, step2, step3]

  return (
    <AnimateInFlex
      animation={ANIMATION_BY_KEY.fade}
      style={{ fontSize: 14 }}
      gap={4}
    >
      {steps.map((step, i) => (
        <Flex key={i} center>
          {getIsStepCompleted(step) ? (
            "✅"
          ) : (
            <Flex
              style={{
                width: 14,
                height: 14,
                backgroundColor: COLORS.GRAY2,
                border: `1px solid ${COLORS.GRAY4}`,
              }}
            />
          )}
        </Flex>
      ))}
    </AnimateInFlex>
  )
}

export const getAggregateStatus = (
  data: TryoutProgressFormData | TryoutConstructor
): TryoutStatus => {
  const { step0, step1, step2, step3 } = getStepProgress(data)

  const step0Complete = getIsStepCompleted(step0)
  const step1Complete = getIsStepCompleted(step1)
  const step2Complete = getIsStepCompleted(step2)
  const step3Complete = getIsStepCompleted(step3)

  if (step0Complete && step1Complete && step2Complete && step3Complete) {
    return TryoutStatus.COMPLETED
  }
  return TryoutStatus.PENDING
}

export const getInitialTryoutProgressFormData = (
  tryout: TryoutConstructor,
  student: StudentConstructor
): TryoutProgressFormData => {
  const {
    primaryAddress,
    guardian1Name,
    guardian1Phone,
    guardian1Email,
    guardian2Name,
    guardian2Phone,
    guardian2Email,
    attendance,
  } = student

  // Step 2's level dropdowns start from the levels that actually apply to
  // the tryout's semester; prefilling from the flat fields would let a
  // stale value be saved straight back onto the student.
  const currentLevels = getAllSkillLevelsAsOf(student, tryout.semester)

  // Handle migration from old 3-step format to new 4-step format
  // Old step1.isTryoutFeePaid moves to step0.isTryoutFeePaid
  // Old step1.isEvalFormCompleted becomes step1.teacherHasEvalForm
  const legacyStep0 = {
    isTryoutFeePaid: get(tryout, "step0.isTryoutFeePaid", false),
    hasSelectedSession: !!tryout.sessionId,
    tryoutFeeId: get(tryout, "step1.tryoutFeeId", undefined),
    tryoutDiscountIds: get(tryout, "step1.tryoutDiscountIds", []),
  }

  // Auto-check release form if student already has a waiver file
  const hasWaiver = !!student.files?.waiver
  const hasSignedReleaseForm =
    get(tryout, "step1.hasSignedReleaseForm", false) || hasWaiver

  const legacyStep1 = {
    isTourCompleted: get(tryout, "step1.isTourCompleted", false),
    // Map old field name to new
    teacherHasEvalForm: get(
      tryout,
      "step1.teacherHasEvalForm",
      get(tryout, "step1.isEvalFormCompleted", false)
    ),
    hasEmergencyContact: get(tryout, "step1.hasEmergencyContact", false),
    hasSignedReleaseForm,
    // Guardian info from student
    primaryAddress,
    guardian1Name,
    guardian1Phone,
    guardian1Email,
    guardian2Name,
    guardian2Phone,
    guardian2Email,
  }

  const legacyStep2 = {
    isEvalScanned: get(tryout, "step2.isEvalScanned", false),
    isLevelUpdated: get(tryout, "step2.isLevelUpdated", false),
    didPassTryout: get(tryout, "step2.didPassTryout", undefined),
    uploadedEval: get(tryout, "step2.uploadedEval", null),
    failureReason: get(tryout, "step2.failureReason", undefined),
    coreSkillLevel: currentLevels[StudentSkillType.core] || undefined,
    digitalSkillLevel: currentLevels[StudentSkillType.digital] || undefined,
    sculptureSkillLevel: currentLevels[StudentSkillType.sculpture] || undefined,
  }

  const enrollmentReceiptSent = get(
    tryout,
    "step3.enrollmentReceiptSent",
    false
  )

  const selectedClassId = get(tryout, "step3.selectedClassId", undefined)
  const selectedSessionIds: Uuid[] = []

  if (selectedClassId) {
    attendance?.[selectedClassId]?.forEach((s) => {
      if (s.status === ATTENDANCE_STATUSES.ENROLLED) {
        selectedSessionIds.push(s.sessionId)
      }
    })
  }

  const legacyStep3 = {
    hasEvalFeedback: get(tryout, "step3.hasEvalFeedback", false),
    hasGivenSwag: get(tryout, "step3.hasGivenSwag", false),
    hasSelectedSessions: get(tryout, "step3.hasSelectedSessions", false),
    selectedSessionIds,
    selectedClassId,
    baselineEnrollmentByClass: get(
      tryout,
      "step3.baselineEnrollmentByClass",
      undefined
    ),
    enrollmentReceiptSent,
    sendReceipt: !enrollmentReceiptSent,
  }

  const initialValues = merge({}, DEFAULT_TRYOUT_PROGRESS_FORM_DATA, tryout, {
    tryoutFilterDate: dayjs(tryout.tryoutFilterDate),
    step0: legacyStep0,
    step1: legacyStep1,
    step2: legacyStep2,
    step3: legacyStep3,
  })

  initialValues.tryoutStatus = getAggregateStatus(initialValues)

  return initialValues
}
